/* @flow weak */
import React from 'react';

import Button from 'components/Button.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import CurrentUser from 'auth/CurrentUser';
import ErrorActions from 'actions/ErrorActions';
import FlexibleBlock from 'components/FlexibleBlock.jsx';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupLink from 'groups/components/GroupLink.jsx';
import GroupName from 'groups/components/GroupName.jsx';
import Subscriber from 'entities/Subscriber';
import Subscription from 'entities/Subscription';
import Text from 'components/Text.jsx';
import Time from 'components/Time.jsx';
import ToastActions from 'actions/ToastActions';
import { mountModal } from 'lib/modal';

import './styles/RelationshipViewGroupListItem.scss';

class RelationshipViewGroupItem extends React.Component {
  static propTypes = {
    group: React.PropTypes.instanceOf(Group).isRequired,
    subscriber: React.PropTypes.instanceOf(Subscriber).isRequired,
    subscription: React.PropTypes.instanceOf(Subscription),
  };

  actionButtonText = () => {
    if (this.isActionRemove()) {
      return 'Remove';
    } else if (this.isActionLeave()) {
      return 'Leave';
    } else {
      return null;
    }
  };

  confirmRemoveFromGroup = (action) => {
    const { group, subscriber } = this.props;
    const groupName = group.getName();
    const isActionRemove = action === 'Remove';

    const confirmAction = isActionRemove ?
      () => GroupActions.removeMember(group, subscriber)
            .then(() => {
              ToastActions.showSuccessToast(
                `${subscriber.getName()} has been removed from ${groupName}`
              );
            }, ToastActions.showDefaultErrorToast) :
      () => GroupActions.unsubscribeFromGroup(group)
            .then(() => {
              ToastActions.showSuccessToast(`You have left ${groupName}`);
            }, ToastActions.showDefaultErrorToast);

    const confirmTitle = isActionRemove ?
      `Are you sure you want to remove ${subscriber.getName()} from ${groupName}?` :
      'Leave this class?';

    const conditionalProps = {};
    if (!isActionRemove) {
      conditionalProps.cancelButtonText = 'Cancel';
      conditionalProps.subtext =
        `Once you leave, you will no longer receive messages from ${groupName}`;
    }

    const dialog = (
      <ConfirmDialog
        eventScreenName="RelationshipViewGroupItem"
        {...conditionalProps}
        title={confirmTitle}
        actionButtonText={action}
        onConfirm={confirmAction}
      />
    );

    mountModal(dialog, this);
  };

  isActionLeave = () => {
    return !this.isActionRemove() && this.props.group.canLeave();
  };

  isActionRemove = () => {
    const { group, subscriber } = this.props;

    if (group.isStaffClass()) {
      return false;
    }

    const user = CurrentUser.getUser();
    const isOwnedBySubscriber = group.isOwnedByUser(subscriber.id);
    const isOwnedByCurrentUser = group.isOwnedByUser(user.id);

    return isOwnedByCurrentUser && !isOwnedBySubscriber;
  };

  joinGroup = () => {
    const onSuccess = () => ToastActions.showSuccessToast('Class joined');
    const onError = error => ErrorActions.serverError(error);
    GroupActions.subscribeToGroup(this.props.group)
      .then(onSuccess, onError);
  };

  renderActionButton = () => {
    const { subscription } = this.props;

    const sharedProps = { level: 'secondary', size: 'xsmall' };

    if (subscription) {
      const actionText = this.actionButtonText();
      return actionText && (
        <Button
          {...sharedProps}
          color="lava"
          onClick={() => this.confirmRemoveFromGroup(actionText)}>
          {actionText}
        </Button>
      );
    } else {
      return (
        <Button
          {...sharedProps}
          color="brand"
          onClick={this.joinGroup}>
          Join
        </Button>
      );
    }
  };

  renderAvatar = () => {
    return (
      <GroupAvatar
        className="RelationshipViewGroupListItem-avatar"
        group={this.props.group}
      />
    );
  };

  renderGroupName = () => {
    const { group } = this.props;

    const text = (
      <Text size="xsmall" weight="bold" truncate>
        <GroupName group={group} />
      </Text>
    );

    if (this.props.subscription) {
      return (
        <GroupLink className="RelationshipViewGroupListItem-name" group={group}>
          {text}
        </GroupLink>
      );
    } else {
      // can't nav to a group you aren't a member of
      return text;
    }
  };

  renderSubText = () => {
    const { subscription, subscriber, group } = this.props;
    const user = CurrentUser.getUser();

    const coOwned = (
      group.isOwnedByUser(user.id) &&
      group.isOwnedByUser(subscriber.id)
    );

    let subText;
    if (coOwned) {
      subText = 'Also owned by you';
    } else if (subscription) {
      subText = (
        <span>
          Joined&nbsp;
          <Time
            date={subscription.getSubscribedAt()}
            month="numeric"
            day="numeric"
            year="numeric"
          />
        </span>
      );
    } else {
      subText = `@${group.getCode()}`;
    }

    return (
      <div className="RelationshipViewGroupListItem-subText">
        {subText}
      </div>
    );
  };

  render() {
    return (
      <FlexibleBlock className="RelationshipViewGroupListItem" flex="left">
        <FlexibleBlock flex="right">
          {this.renderAvatar()}
          <div className="RelationshipViewGroupListItem-info">
            {this.renderGroupName()}
            {this.renderSubText()}
          </div>
        </FlexibleBlock>
        {this.renderActionButton()}
      </FlexibleBlock>
    );
  }
}

export default RelationshipViewGroupItem;



// WEBPACK FOOTER //
// ./src/user/components/RelationshipViewGroupListItem.jsx